/**
 * How hard the page tries to get its extension host back (见 18 连接生命周期).
 *
 * A lost host used to be terminal for the tab: the page showed a Retry button and
 * waited to be clicked. The common cause needs no human, though — the browser
 * reclaimed the SharedWorker after sleep or under memory pressure, and simply
 * constructing it again brings a fresh one up — so the page reconnects on its own
 * and keeps the button for when that does not work.
 *
 * Attempts are **bounded**, which is the whole reason this is a decision rather
 * than a loop. Retrying forever would bury the failures that are not transient (a
 * worker script that throws at load, an origin serving two deploys) under a
 * spinner that never resolves; running out of attempts puts the tab back on the
 * explicit disconnected screen, where the reason is on display.
 *
 * Kept free of DOM types so the schedule is unit-testable — same shape and same
 * reason as `generation.ts` and `sw-control.ts`.
 */

/** `retry` = try again after `delayMs`. `give-up` = show the disconnected screen. */
export type ReconnectDecision =
  | { kind: 'retry'; delayMs: number }
  | { kind: 'give-up' };

/**
 * Delay before each successive attempt.
 *
 * The first is short enough to be invisible in the case this exists for — a
 * reclaimed worker restarts immediately, so the tab recovers before the user
 * finishes looking at it. Later ones back off because a host that failed to come
 * up twice is unlikely to manage it on the third try a moment later, and a tight
 * loop of `new SharedWorker` re-fetching a broken script is its own problem. The
 * total budget is around 15s, past which waiting silently is worse than saying so.
 */
export const RECONNECT_DELAYS_MS: readonly number[] = [
  500, 1000, 2000, 4000, 8000,
];

/**
 * How long a reconnected host must stay alive before it earns a fresh retry
 * budget.
 *
 * `hibro:ready` only proves that the port was attached. Startup activation and
 * storage probes continue afterwards, so resetting at `ready` lets a host that
 * repeatedly attaches and then dies retry forever. A genuinely healthy host
 * easily outlives this window; an occasional later loss still gets the complete
 * schedule.
 */
export const RECONNECT_STABLE_MS = 30_000;

/**
 * Decide whether to reconnect again, given how many attempts have already been
 * made since the last time this page was connected.
 *
 * `attempt` counts from 0 (the host was just lost, nothing tried yet) and is
 * reset only after the replacement host survives {@link RECONNECT_STABLE_MS}.
 * That keeps the full budget for an occasional loss without letting a
 * short-lived host refill it merely by sending `hibro:ready`.
 */
export function decideReconnect(input: { attempt: number }): ReconnectDecision {
  const delayMs = RECONNECT_DELAYS_MS[input.attempt];
  return delayMs === undefined
    ? { kind: 'give-up' }
    : { kind: 'retry', delayMs };
}
